import React, { useEffect } from 'react'

const TermsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-sm text-gray-600 mb-8">Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. IMPORTANT FINANCIAL DISCLAIMERS</h2>
            
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
              <h3 className="text-lg font-bold text-red-900 mb-2">NOT FINANCIAL ADVICE</h3>
              <p className="text-red-800">
                THE INFORMATION PROVIDED BY STOCKSINES ("WE," "US," OR "OUR") ON STOCKSINES.COM (THE "SITE") IS FOR GENERAL INFORMATIONAL AND EDUCATIONAL PURPOSES ONLY. ALL INFORMATION ON THE SITE IS PROVIDED IN GOOD FAITH; HOWEVER, WE MAKE NO REPRESENTATION OR WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, REGARDING THE ACCURACY, ADEQUACY, VALIDITY, RELIABILITY, AVAILABILITY, OR COMPLETENESS OF ANY INFORMATION ON THE SITE.
              </p>
              <p className="text-red-800 mt-3">
                UNDER NO CIRCUMSTANCE SHALL WE HAVE ANY LIABILITY TO YOU FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF THE USE OF THE SITE OR RELIANCE ON ANY INFORMATION PROVIDED ON THE SITE. YOUR USE OF THE SITE AND YOUR RELIANCE ON ANY INFORMATION ON THE SITE IS SOLELY AT YOUR OWN RISK.
              </p>
            </div>

            <h3 className="text-xl font-semibold mb-3">1.1 No Professional Relationship</h3>
            <p className="mb-4">
              The Site does not provide financial, investment, trading, tax, legal, or any other professional advice. The Site does not create any professional services relationship between you and StockSines. You should not make any financial or investment decision based solely on information found on the Site.
            </p>

            <h3 className="text-xl font-semibold mb-3">1.2 Educational Purpose Only</h3>
            <p className="mb-4">
              The Site provides tools and visualizations of options market data for educational and informational purposes only. These tools are designed to help users understand market sentiment and options flow patterns but should not be used as the sole basis for making investment decisions.
            </p>

            <h3 className="text-xl font-semibold mb-3">1.3 Consult Professional Advisors</h3>
            <p className="mb-4">
              You should consult your financial advisor, attorney, tax professional, or other professional advisors regarding your specific situation before making any investment or financial decisions. Any action you take upon the information on the Site is strictly at your own risk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. ACCEPTANCE OF TERMS</h2>
            <p className="mb-4">
              By accessing and using this Site, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. USE LICENSE AND RESTRICTIONS</h2>
            <h3 className="text-xl font-semibold mb-3">3.1 License Grant</h3>
            <p className="mb-4">
              Permission is granted to temporarily access the materials (information or software) on StockSines for personal, non-commercial transitory viewing only.
            </p>

            <h3 className="text-xl font-semibold mb-3">3.2 License Restrictions</h3>
            <p className="mb-4">Under this license you may not:</p>
            <ul className="list-disc ml-6 mb-4">
              <li>Modify or copy the materials</li>
              <li>Use the materials for any commercial purpose or for any public display</li>
              <li>Attempt to reverse engineer any software contained on StockSines</li>
              <li>Remove any copyright or other proprietary notations from the materials</li>
              <li>Scrape, harvest, or otherwise collect data from the Site using automated means</li>
              <li>Create derivative works based on the Site's content or functionality</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. MARKET DATA AND ACCURACY</h2>
            <h3 className="text-xl font-semibold mb-3">4.1 Data Sources</h3>
            <p className="mb-4">
              The options market data displayed on the Site is derived from various sources and processed using proprietary algorithms. While we strive for accuracy, we cannot guarantee that all information is accurate, complete, or current at all times.
            </p>

            <h3 className="text-xl font-semibold mb-3">4.2 No Warranty on Data</h3>
            <p className="mb-4">
              We make no warranties or representations about the accuracy or completeness of the Site's content or the content of any sites linked to this Site. Market data may be delayed, inaccurate, or contain errors. You acknowledge that any reliance on such data is at your sole risk.
            </p>

            <h3 className="text-xl font-semibold mb-3">4.3 Hypothetical or Simulated Performance</h3>
            <p className="mb-4">
              Any references to past market performance or hypothetical trading results have inherent limitations. Past performance is not indicative of future results. The Site may contain hypothetical or simulated performance results that have certain inherent limitations and should not be relied upon for actual trading decisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. RISK DISCLOSURE</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-6">
              <h3 className="text-lg font-bold text-yellow-900 mb-2">TRADING RISKS</h3>
              <p className="text-yellow-800">
                Trading securities, options, futures, and other financial instruments involves substantial risk of loss and is not suitable for every investor. The valuation of these instruments may fluctuate, and as a result, you may lose more than your original investment. The highly leveraged nature of options trading means that small market movements can have a disproportionately large impact on your investment.
              </p>
            </div>

            <p className="mb-4">
              You should carefully consider whether trading is suitable for you in light of your circumstances, knowledge, and financial resources. You may lose all or more than your initial investment. Opinions, market data, and recommendations are subject to change at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. LIMITATION OF LIABILITY</h2>
            <p className="mb-4">
              In no event shall StockSines or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption, personal injury, loss of privacy, or any other pecuniary loss) arising out of the use or inability to use the materials on StockSines, even if StockSines or a StockSines authorized representative has been notified orally or in writing of the possibility of such damage.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. INDEMNIFICATION</h2>
            <p className="mb-4">
              You agree to indemnify and hold harmless StockSines, its officers, directors, employees, agents, and third parties, for any losses, costs, liabilities, and expenses (including reasonable attorney's fees) relating to or arising out of your use of or inability to use the Site or services, any user postings made by you, your violation of any terms of this Agreement or your violation of any rights of a third party, or your violation of any applicable laws, rules, or regulations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. PRIVACY AND DATA COLLECTION</h2>
            <p className="mb-4">
              Your use of our Site is also governed by our Privacy Policy. Please review our Privacy Policy, which also governs the Site and informs users of our data collection practices. We collect limited information including email addresses for our waitlist and use cookies for site functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. INTELLECTUAL PROPERTY</h2>
            <p className="mb-4">
              The Site and its original content, features, and functionality are owned by StockSines and are protected by international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws. Our proprietary algorithms, data processing methods, and visualization techniques are confidential and proprietary to StockSines.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. TERMINATION</h2>
            <p className="mb-4">
              We may terminate or suspend your access to our Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. GOVERNING LAW</h2>
            <p className="mb-4">
              These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. CHANGES TO TERMS</h2>
            <p className="mb-4">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. CONTACT INFORMATION</h2>
            <p className="mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="mb-4">
              Email: support@stocksines.com<br/>
              Website: https://stocksines.com
            </p>
          </section>

          <div className="mt-12 p-6 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              By using StockSines, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsPage